import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import {
	checkValidation,
	getHelperText,
	ISepaInfo,
	OfferContext,
	PayOneService,
	removeAllWhitespaces,
	getLabelValue,
} from '@adac-mp/shared';
import { Typography, Input } from '../../index';
import { usePreviousValue } from '../../hooks/usePreviousValue';
import { useValidatedStates } from '../../hooks/useValidatedStates';

interface IIbanInputProps {
	validateFields?: boolean;
	isDkv?: boolean;
	updateSepaInfo?: (sepaInfo: ISepaInfo) => void;
	setSepaInfoFilled?: (sepaInfoFilles: boolean) => void;
}

export const IbanInput = React.memo(
	({ validateFields, updateSepaInfo = () => {}, setSepaInfoFilled = () => {}, isDkv }: IIbanInputProps) => {
		const stateInit = {
			iban: {
				value: '',
				error: false,
				required: true,
			},
		};

		const { lang, labels } = useContext(OfferContext);
		const [state, { updateWithError, updateWithoutError }] = useValidatedStates(stateInit);
		const [isLoading, setIsLoading] = useState(false);
		const [bankName, setBankName] = useState('');
		const prevIbanValue = usePreviousValue(state.iban.value);

		const checkIban = async () => {
			let isValid = false;
			if (prevIbanValue === state.iban.value) {
				return;
			}
			if (checkValidation(removeAllWhitespaces(state.iban.value), 'IBAN')) {
				updateWithError('iban', state.iban.value, true);
				return;
			}
			setIsLoading(true);
			const iban = removeAllWhitespaces(state.iban.value);
			const { code, openIbanInfo } = await PayOneService.checkIban({ iban, dkv: Boolean(isDkv) }, lang);
			if (code === 500) {
				updateWithError('iban', state.iban.value, true);
			}
			if (code === 200) {
				updateWithError('iban', state.iban.value, !openIbanInfo.valid);
				if (openIbanInfo.valid) {
					isValid = true;
					setBankName(openIbanInfo.bankData.name || 'Gültige IBAN');
					updateSepaInfo({
						iban: iban.replace(/\s/g, ''),
					});
				}
			}
			setSepaInfoFilled(isValid);
			setIsLoading(false);
		};

		const setIbanHelperText = () => {
			if (state.iban.error) return getHelperText('IBAN');
			if (bankName)
				return (
					<>
						{bankName !== 'Gültige IBAN' && (
							<Typography data-cy="helperText: Bank name" variant="title" className="after:content-['_']">
								{getLabelValue('mp.ibanInputBankName', labels) || 'Name der Bank:'}
							</Typography>
						)}
						{bankName}
					</>
				);
			return '';
		};

		const handleValueChanged = (e: ChangeEvent<HTMLInputElement>) => {
			let position = e.target.selectionEnd;
			const length = e.target.value.length;
			const formattedIbanValue = e.target.value
				.replace(/[^\dA-Za-z]/g, '')
				.replace(/(.{4})/g, '$1 ')
				.toUpperCase()
				.trim();
			updateWithoutError('iban', formattedIbanValue);
			setBankName('');
			// programatically set position of cursor
			// workaround as it doesn't work without the timeout
			setTimeout(() => {
				if (position && position !== formattedIbanValue.length) {
					e.target.selectionEnd = position +=
						e.target.value.charAt(position - 1) === ' ' &&
						e.target.value.charAt(length - 1) === ' ' &&
						length !== e.target.value.length
							? 1
							: 0;
				}
			}, 0);
		};

		useEffect(() => {
			if (validateFields) {
				const hasError = !state.iban.value || state.iban.error;
				updateWithError('iban', state.iban.value, hasError);
			}
		}, [validateFields]);

		return (
			<form
				className="pt-4"
				autoComplete="off"
				onSubmit={(ev) => {
					ev.preventDefault();
				}}>
				<Typography color="title" className="px-4 mb-3" component="p">
					{getLabelValue('mp.ibanInputBankData', labels) || 'Geben Sie hierfür ihre Bankdaten an:'}
				</Typography>
				<Input
					type="text"
					className="uppercase"
					disabled={isLoading}
					loading={isLoading}
					placeholder="IBAN"
					data-cy="input: Iban"
					maxLength={38} //max IBAN length + whitespaces
					value={state.iban.value}
					hasError={state.iban.error}
					hasSucceeded={bankName && state.iban.value && !state.iban.error}
					helperText={setIbanHelperText()}
					onBlur={checkIban}
					onChange={(e) => {
						handleValueChanged(e);
					}}
				/>
			</form>
		);
	},
);

IbanInput.displayName = 'IbanInput';
